import type { components } from '@/generated/contracts/api-v1';
import { fieldMap, requireId, type FieldSpec, type LegacyRow } from './fieldMap';

// scene_instances table; SceneDto.From in lureo-backend Data/Api/V1/Scenes/ScenesDtos.cs
export type SceneDto = components['schemas']['SceneDto'];
export type SceneStatus = components['schemas']['SceneStatus'];

const spec = {
  uniqueId: 'scene_instance_id',
  storyUniqueId: 'scene_origin_id',
  projectUniqueId: 'project_instance_id',
  sceneTypeUniqueId: 'scene_type_id',
  userUniqueId: 'user_id',
  displayName: 'scene_instance_display_name',
  description: 'scene_instance_description',
  // Stored lowercase, which is the v1 value; a null column reads as draft
  status: 'scene_instance_status',
  versionNumber: 'scene_instance_version_number',
  anchorCategory: 'scene_instance_anchor_category',
  coverImgUrl: 'scene_instance_cover_img_url',
  // A null column reads as false
  userHasUploadedCoverImage: 'scene_instance_user_has_uploaded_cover_image',
  durationMs: 'scene_instance_duration_ms',
  // A null column reads as false
  isStartingScene: 'scene_instance_is_starting_scene',
  sourceName: 'scene_instance_source_name',
  activeView: 'scene_instance_active_view',
  originalRoot: 'scene_instance_original_root',
  // Only a JSON object survives; any other stored JSON reads as null
  locationInfo: { legacy: 'scene_instance_location_info', as: 'jsonText' },
  payload: { legacy: 'scene_instance_payload', as: 'jsonText' },
  createdAt: 'created_at',
  updatedAt: 'updated_at',
} as const satisfies FieldSpec<SceneDto>;

const map = fieldMap<SceneDto, typeof spec>('Scene', spec);

export type LegacyScene = LegacyRow<typeof spec, SceneDto>;

/** updateScene accepts these. Status changes go through changeSceneStatus, never PATCH. */
export const SCENE_PATCH_FIELDS = ['displayName', 'coverImgUrl', 'payload', 'anchorCategory', 'durationMs', 'sceneTypeUniqueId'] as const;

export const sceneToLegacy = (dto: SceneDto): LegacyScene => map.toLegacy(dto);

/** updateScene body from a legacy partial update; updated_at is stamped by the server. */
export const scenePatchBody = (changes: Partial<LegacyScene>) => map.toBody(changes, SCENE_PATCH_FIELDS, ['updated_at']);

/**
 * The first version inside a createStory body, from the legacy scene_instances insert row;
 * scene_instance_id is required. The server writes the owner, project, story and timestamps, so
 * those columns are skipped. It always makes version 1, a draft named after the story, so a row
 * carrying a status, version number or display name is refused rather than silently changed.
 */
export function sceneFirstVersionBody(row: Partial<LegacyScene>): components['schemas']['CreateStorySceneRequest'] {
  const body = map.toBody(row, ['uniqueId', 'payload', 'anchorCategory'], ['user_id', 'project_instance_id', 'scene_origin_id', 'created_at', 'updated_at']);
  return { ...body, uniqueId: requireId('Scene', 'scene_instance_id', body.uniqueId) };
}
